import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { updateItemMenu } from '../../../../../store/actions/menuActions';
import Module from '../../../../layout/Module';
import {
  fillASurveyAbout,
  getMySurveys,
  getSurveyByIdAbout
} from '../../../../../services/data-entry/surveys.service';
import InfoAboutData from '../../components/InfoAboutData/InfoAboutData';
import { Box, Grid, Skeleton, Typography, CircularProgress } from '@mui/material';
import SurveyQuestionCard from '../SurveyQuestionCard/SurveyQuestionCard';
import { useDispatch, useSelector } from 'react-redux';
import { alertReset, toggleAlertModule } from '../../../../../store/actions/alert.actions';
import ButtonStartIcon from '../../../../button/ButtonStartIcon';
import AlertSaveButton from '../../../../button/AlertSaveButton';
import {
  prepateData,
  resetSurveyToFillErrors,
  resetSurveyToFillState,
  updateSurveyToFillData,
  updateSurveyToFillQuestion
} from '../../../../../store/actions/dataEntryFillASurveyActions';
import { useSnackbar } from 'notistack';
import Loading from '../../../../Loading/Loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DialogConfirmCancel from '../../../../dialog/DialogConfirm';
import useFetchData from '../../../../../hooks/useFetchData';

const FillASurveyAbout = () => {
  let questionIndex = 0;
  const saveRef = useRef(null);
  const navigate = useNavigate();

  const { surveyId, recordId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [hasExpired, setHasExpired] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autoSaveTimer, setAutoSaveTimer] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const questionsRef = useRef([]);
  const hasChangedRef = useRef(false);
  const isSubmittingRef = useRef(false); // Add ref to track submitting state
  const { data: dataYears = [] } = useFetchData('setting/year');

  const questions = useSelector(
    (state) => state.dataEntryFillASurvey?.fillASurvey?.data?.template?.questions ?? []
  );
  const alertSave = useSelector((state) => state.alert?.save ?? false);
  const permissions = useSelector((state) => state?.session?.permissions?.dataentrymysurveys ?? {});
  const dispatch = useDispatch();

  const onAlert = (show, value, onSave, onDontSave) => {
    dispatch(toggleAlertModule(show, 'survey', value, onSave, onDontSave));
  };

  const { enqueueSnackbar } = useSnackbar();

  const autoSaveSurvey = async () => {
    try {
      // Check if we're submitting - if so, skip auto-save
      if (isSaving || isSubmittingRef.current || isCompleted || hasExpired || data?.status === 'draft')
        return;

      setIsSaving(true);
      setIsEditing(false);
      dispatch(resetSurveyToFillErrors());

      const questionsToSave = questions?.filter((question) => question?.answer?.length);

      if (questionsToSave.length > 0) {
        const surveyAnswers = questionsToSave?.map((question) => {
          const { id, answer } = question;
          return { questionId: id, answer };
        });

        const payload = { surveyAnswers, isSubmitting: false };

        const { error } = await fillASurveyAbout(surveyId, recordId, payload);

        if (!error) {
          if (alertSave && alertSave.mode) {
            dispatch(alertReset());
            dispatch(toggleAlertModule());
          }
          hasChangedRef.current = false;
        }
      }
    } catch (err) {
      console.error('Auto-save error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const scheduleAutoSave = useCallback(() => {
    // Don't schedule auto-save if submitting
    if (isSubmittingRef.current) return;

    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
    }

    setIsEditing(true);

    const newTimer = setTimeout(() => {
      autoSaveSurvey();
    }, 3000); // 3 seconds

    setAutoSaveTimer(newTimer);
  }, [autoSaveSurvey]);

  const onChangeAlert = useCallback(() => {
    if (alertSave && !alertSave.mode) {
      onAlert(false, 'adding', () => {
        if (autoSaveTimer) {
          clearTimeout(autoSaveTimer);
          autoSaveSurvey();
        }
      });
    }
  }, [onAlert, alertSave, autoSaveTimer, autoSaveSurvey]);

  const renderStatusIndicator = () => {
    if (isSaving) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: 2,
            bgcolor: '#0288d1',
            color: 'white',
            boxShadow: '0 2px 8px rgba(2, 136, 209, 0.25)',
            animation: 'fadeIn 0.3s ease-in'
          }}
        >
          <CircularProgress size={16} sx={{ color: 'white' }} />
          <Typography variant='body2' sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
            Saving...
          </Typography>
        </Box>
      );
    }

    if (isEditing) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: 2,
            bgcolor: '#fff3e0',
            color: '#e65100',
            border: '1px solid #ffb74d',
            animation: 'fadeIn 0.3s ease-in'
          }}
        >
          <FontAwesomeIcon
            icon={['fal', 'pencil']}
            style={{ fontSize: '14px', animation: 'wiggle 1s ease-in-out infinite' }}
          />
          <Typography variant='body2' sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
            Editing...
          </Typography>
        </Box>
      );
    }

    return null;
  };

  useEffect(() => {
    const currentQuestionsString = JSON.stringify(questions);
    const previousQuestionsString = JSON.stringify(questionsRef.current);

    if (currentQuestionsString !== previousQuestionsString && questions.length > 0) {
      questionsRef.current = questions;
      hasChangedRef.current = true;

      // Don't schedule auto-save if we're submitting
      if (!loading && !isCompleted && !hasExpired && data?.status !== 'draft' && !isSubmittingRef.current) {
        scheduleAutoSave();
      }
    }
  }, [questions, loading, isCompleted, hasExpired, data?.status, scheduleAutoSave]);

  useEffect(() => {
    return () => {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
    };
  }, [autoSaveTimer]);

  useEffect(() => {
    return () => {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
      // Reset submitting ref on unmount
      isSubmittingRef.current = false;
    };
  }, []);

  // Add CSS animations
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateY(-5px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes wiggle {
        0%, 100% {
          transform: rotate(0deg);
        }
        25% {
          transform: rotate(-5deg);
        }
        75% {
          transform: rotate(5deg);
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    const onLoad = async () => {
      try {
        const response = await getSurveyByIdAbout(surveyId, recordId);
        if (!response?.data && response?.error) {
          setError(response.error.message || 'An error occurred');
          return;
        } else {
          setData({
            data: response?.data?.data,
            template: response?.data?.template?.title,
            name: response?.data?.name,
            type: response?.data?.type,
            status: response?.data?.status,
            surver: response?.data?.name
          });
          setIsCompleted(response?.data?.isCompleted);
          setHasExpired(response?.data?.hasExpired);
          setIsSubmitting(response?.data?.isSubmitting);
          const preparedData = prepateData({
            ...response?.data,
            answers: response?.data?.result?.answers
          });
          if (preparedData) {
            dispatch(updateSurveyToFillData(preparedData));
            return;
          } else {
            dispatch(updateSurveyToFillData(null));
          }
        }
      } catch (err) {
        setError({
          code: err?.code || 'ERROR',
          message: err.message || 'Something went wrong. Please try again.'
        });
      } finally {
        setLoading(false);
      }
    };

    onLoad();
  }, [surveyId, recordId, dispatch]);

  const isValidForm = () => {
    dispatch(resetSurveyToFillErrors());

    for (const question of questions) {
      const { id, answer, required, type, title } = question;
      const isGroupReference = type?.value === 'groupreference';

      if (required && (!answer || answer.length === 0) && !isGroupReference) {
        dispatch(
          updateSurveyToFillQuestion({
            questionId: id,
            newData: { error: 'Your answer is required for this question' }
          })
        );
        return false;
      }

      if (isGroupReference) {
        dispatch(
          updateSurveyToFillQuestion({
            questionId: id,
            newData: { answer: [title || id], error: null }
          })
        );
      }
    }

    return true;
  };

  const handleSubmitSurvey = async () => {
    try {
      // Set submitting flag and clear any pending auto-save
      isSubmittingRef.current = true;
      setIsSubmitting(true);
      setIsEditing(false);

      // Cancel any pending auto-save timers
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
        setAutoSaveTimer(null);
      }

      // Wait a moment to ensure any in-flight auto-save completes
      if (isSaving) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      const validForm = isValidForm();

      if (validForm) {
        const surveyAnswers = questions?.map((question) => {
          const { id, answer } = question;
          return { questionId: id, answer };
        });

        const payload = { surveyAnswers, isSubmitting: true };

        const { error } = await fillASurveyAbout(surveyId, recordId, payload);

        if (error) {
          enqueueSnackbar(error.message || 'We could not save your answers, please try again', {
            variant: 'error'
          });
          // Reset submitting flag on error
          isSubmittingRef.current = false;
        } else {
          dispatch(alertReset());
          dispatch(toggleAlertModule());
          const { data } = await getMySurveys(0, 1000, { isCompleted: false, hasExpired: false });
          const totalRecords = data?.totalRecords ?? 0;
          dispatch(
            updateItemMenu({
              item: 'dataentry',
              subItem: 'dataentrymysurveys',
              data: { showNotification: totalRecords > 0, badgeContent: totalRecords }
            })
          );
          dispatch(resetSurveyToFillState());
          setShowDeleteModal(false);
          navigate(`/dashboard/data-entry/my-surveys`);
        }
      } else {
        // Reset submitting flag if validation fails
        isSubmittingRef.current = false;
      }
    } catch (err) {
      setShowDeleteModal(false);
      enqueueSnackbar(err.message || 'We could not save your answers, please try again', {
        variant: 'error'
      });
      // Reset submitting flag on error
      isSubmittingRef.current = false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDeleteModal = () => {
    const validForm = isValidForm();

    if (validForm) {
      setShowDeleteModal(true);
    }
  };

  return (
    <Module>
      <Module.Header
        title={
          data
            ? `Fill a survey about ${data?.type?.replace('about-', '')} - ${data?.name}`
            : 'Fill a survey about'
        }
        titleLink={`/dashboard/data-entry/my-surveys/${surveyId}/fill-a-survey-about/${recordId}`}
      />
      <Module.BodyFullContainer className={isSubmitting ? 'relative' : 'overflow-auto relative'}>
        {error ? (
          <div className='flex  justify-center items-center h-full'>
            <div className='flex flex-col justify-center items-center h-full'>
              <Box
                sx={{ width: 300, height: 300 }}
                className='flex flex-col justify-center items-center px-8 py-2 border border-2 border-muted rounded-lg'
              >
                <FontAwesomeIcon
                  icon={['fal', 'exclamation-triangle']}
                  size='9x'
                  className=' text-secondary'
                />
                <Typography className='text-2xl my-2 opacity-50 text-center'>
                  {error?.message || 'An error occurred'}
                </Typography>
                <AlertSaveButton
                  submodule='Surveys'
                  disabled={loading}
                  icon={['fal', 'check']}
                  title='Ok'
                  color='error'
                  variant='outlined'
                  className='shadow-sm mt-2'
                  onDontSave={() => navigate('/dashboard/data-entry/my-surveys')}
                />
              </Box>
            </div>
          </div>
        ) : (
          <>
            <div className='sticky top-0px right-0 bg-background border-b border-2 border-primary z-10 flex justify-between items-center mb-2'>
              <div>
                <InfoAboutData
                  type={data?.type}
                  data={{ ...data?.data, survey: data?.name }}
                  loading={loading}
                  dataYears={dataYears}
                />
              </div>
              <div className='flex justify-end items-center gap-2'>
                {/* Status Indicator */}
                {!loading && !isCompleted && !hasExpired && !error && data?.status !== 'draft' && (
                  <Box sx={{ mr: 1 }}>{renderStatusIndicator()}</Box>
                )}

                {!loading && !isCompleted && !hasExpired && !error && data?.status !== 'draft' ? (
                  <>
                    {permissions?.marking && (
                      <>
                        <ButtonStartIcon
                          size='small'
                          onClick={handleOpenDeleteModal}
                          disabled={isSubmitting || isSaving}
                          startIcon={'circle-check'}
                          ref={saveRef}
                          loading={isSubmitting}
                        >
                          {isSubmitting ? 'Submitting...' : 'Submit'}
                        </ButtonStartIcon>
                      </>
                    )}
                  </>
                ) : (
                  <></>
                )}
                <AlertSaveButton
                  submodule='Surveys'
                  disabled={loading || isSubmitting || isSaving}
                  icon={['fal', 'times']}
                  title='Cancel'
                  color='primary'
                  variant='outlined'
                  className='shadow-sm'
                  onDontSave={() => navigate('/dashboard/data-entry/my-surveys')}
                />
              </div>
            </div>
            <Grid container rowspacing={1} columnspacing={{ xs: 1, sm: 2, md: 3 }} className='relative'>
              {isSubmitting && (
                <div className='absolute top-0 bottom-0 left-0px right-0 bg-loading z-12'>
                  <Loading />
                </div>
              )}
              {loading ? (
                [...Array(5).keys()].map((item) => (
                  <Grid item xs={12} key={item} className='py-2 mt-6'>
                    <Skeleton variant='rectangular' width={'100%'} height={120} />
                  </Grid>
                ))
              ) : questions && questions.length ? (
                questions?.map((item, index) => {
                  if (item?.type?.value !== 'groupreference') {
                    questionIndex += 1;
                  }
                  return (
                    <SurveyQuestionCard
                      key={index + 1}
                      question={item}
                      index={questionIndex}
                      disabled={isSubmitting || isCompleted || hasExpired || data?.status === 'draft'}
                      onChangeAlert={onChangeAlert}
                    />
                  );
                })
              ) : (
                <></>
              )}
            </Grid>
          </>
        )}
      </Module.BodyFullContainer>
      {showDeleteModal && (
        <DialogConfirmCancel
          id={`fill-a-survey-confirm-modal`}
          open={showDeleteModal}
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={handleSubmitSurvey}
          loading={isSubmitting}
          title={'Submit survey'}
          onCloseHeader={true}
          maxWidth='xs'
        >
          <Typography component={'div'}>
            Are you sure you would like to submit this survey? It cannot be edited after submission.
          </Typography>
        </DialogConfirmCancel>
      )}
    </Module>
  );
};

export default FillASurveyAbout;
